import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("auth.resetInvalid"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.resetMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || t("auth.resetInvalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 py-20">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">
        {t("auth.resetTitle")}
      </div>
      <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">
        {t("auth.resetTitle")}
      </h1>

      {done ? (
        <div
          data-testid="reset-password-success"
          className="mt-8 border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 text-sm"
        >
          {t("auth.resetSuccess")}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest">
              {t("auth.newPassword")}
            </label>
            <input
              data-testid="reset-password-new"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full h-11 px-3 border border-black/15 focus:border-electric focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest">
              {t("auth.confirmPassword")}
            </label>
            <input
              data-testid="reset-password-confirm"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full h-11 px-3 border border-black/15 focus:border-electric focus:outline-none"
            />
          </div>
          {error && (
            <div data-testid="reset-password-error" className="text-sm font-mono text-destructive">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="reset-password-submit"
            className="w-full h-12 bg-ink text-white hover:bg-electric disabled:opacity-50 transition-colors text-xs font-mono uppercase tracking-[0.2em]"
          >
            {loading ? t("auth.resetting") : t("auth.resetBtn")}
          </button>
        </form>
      )}

      <div className="mt-6 text-sm">
        <Link to="/login" data-testid="reset-password-back" className="text-electric underline">
          {t("auth.backToSignIn")}
        </Link>
      </div>
    </div>
  );
}
